import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * SearchBar — the FAB modal command-palette per E-023 §H.
 *
 * Modal opens centered (focus-trapped on input). Results grouped by source,
 * arrow-key navigable, ⏎ to open, ⎋ to close, ⌘⏎ to open in new tab.
 *
 * Accessibility (mandatory per E-023 §H):
 *   - role="combobox" on input wrapper, aria-expanded, aria-autocomplete
 *   - role="listbox" on results, role="option" on each result
 *   - aria-activedescendant tracks the highlighted item
 *   - aria-live="polite" announces result count to screen readers
 *   - Focus trap inside modal; Escape closes and restores focus to opener
 *   - All actions reachable by keyboard alone
 *   - Color contrast (relies on consumer's Tailwind tokens — bar uses
 *     ink/paper/surface/line/accent/mute classes that should map to a 4.5:1
 *     contrast palette in the consumer's theme)
 *
 * Drop-in API: takes `open` and `onClose` props, mirroring the existing
 * Cleic Ledger CommandPalette signature so swapping is a one-line change.
 */
import { useCallback, useEffect, useId, useMemo, useRef, } from "react";
import { useSearchBar } from "./useSearchBar.js";
import { registerShortcuts, modLabel } from "../shortcuts/index.js";
const TIP_TEXT = "Use book: to filter · - to exclude · \"phrase\" for exact · ↑↓ navigate · ⏎ open · ⎋ close";
const ACTION_SCORE_BASE = 1000000;
const EMPTY_SIGNALS = {
    booleanMatchCount: 0,
    fuzzyScore: 0,
    recencyDecay: 0,
    accessFrequency: 0,
    sourceWeight: 0,
    fieldWeightSum: 0,
};
function normalizeActionItem(hit, index) {
    return {
        ...hit,
        kind: "action",
        score: ACTION_SCORE_BASE - index,
        signals: EMPTY_SIGNALS,
    };
}
function actionHref(hit) {
    return hit.action?.href ?? hit.action?.route;
}
function matchesActionQuery(hit, query) {
    const terms = query
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean);
    if (terms.length === 0)
        return true;
    const haystack = [
        hit.sourceId,
        hit.recordId,
        hit.primary,
        hit.secondary ?? "",
        hit.action?.intent ?? "",
        hit.action?.label ?? "",
        hit.quickAdd?.surface ?? "",
        hit.quickAdd?.label ?? "",
        ...hit.matchedFields,
    ]
        .join(" ")
        .toLowerCase();
    return terms.every((term) => haystack.includes(term.replace(/^[^:]+:/, "")));
}
export function SearchBar(props) {
    const { open, onClose, mount, ctx, searchFn, placeholder = "Search…", perGroup = 5, limit = 20, actionItems = [], onResultOpen, onResultOpenNewTab, } = props;
    const inputRef = useRef(null);
    const listboxId = useId();
    const previousFocusRef = useRef(null);
    const sb = useSearchBar({ ctx, searchFn, mount, limit });
    const staticActions = useMemo(() => actionItems.map((hit, index) => normalizeActionItem(hit, index)), [actionItems]);
    const actionHits = useMemo(() => staticActions.filter((hit) => matchesActionQuery(hit, sb.query)), [staticActions, sb.query]);
    const hits = useMemo(() => [...actionHits, ...sb.hits], [actionHits, sb.hits]);
    const active = sb.highlightedIndex >= 0 && sb.highlightedIndex < hits.length
        ? hits[sb.highlightedIndex]
        : null;
    // Reset state when the bar closes.
    useEffect(() => {
        if (!open) {
            sb.reset();
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
    // Restore focus on close.
    useEffect(() => {
        if (open) {
            previousFocusRef.current = document.activeElement ?? null;
            // Focus the input after paint.
            const t = setTimeout(() => inputRef.current?.focus(), 10);
            return () => clearTimeout(t);
        }
        else {
            previousFocusRef.current?.focus?.();
        }
        return undefined;
    }, [open]);
    useEffect(() => {
        if (!open)
            return;
        if (hits.length === 0) {
            if (sb.highlightedIndex !== -1)
                sb.setHighlightedIndex(-1);
            return;
        }
        if (sb.highlightedIndex < 0 || sb.highlightedIndex >= hits.length) {
            sb.setHighlightedIndex(0);
        }
    }, [open, hits.length, sb.highlightedIndex, sb.setHighlightedIndex]);
    // Lock body scroll while open.
    useEffect(() => {
        if (!open)
            return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);
    // Wire shortcuts.
    const isOpenRef = useRef(open);
    isOpenRef.current = open;
    const handleOpen = useCallback((hit) => {
        onResultOpen?.(hit);
        if (!onResultOpen) {
            const href = actionHref(hit);
            if (href)
                window.location.assign(href);
        }
        onClose();
    }, [onResultOpen, onClose]);
    const handleOpenNewTab = useCallback((hit) => {
        if (onResultOpenNewTab) {
            onResultOpenNewTab(hit);
        }
        else {
            const href = actionHref(hit);
            if (href)
                window.open(href, "_blank", "noopener,noreferrer");
        }
        onClose();
    }, [onResultOpenNewTab, onClose]);
    const handleSelect = useCallback(() => {
        if (active)
            handleOpen(active);
    }, [active, handleOpen]);
    const handleNewTab = useCallback(() => {
        if (active)
            handleOpenNewTab(active);
    }, [active, handleOpenNewTab]);
    const handleNext = useCallback(() => {
        if (hits.length === 0) {
            sb.setHighlightedIndex(-1);
            return;
        }
        if (sb.highlightedIndex < 0) {
            sb.setHighlightedIndex(0);
            return;
        }
        sb.setHighlightedIndex(Math.min(sb.highlightedIndex + 1, hits.length - 1));
    }, [hits.length, sb.highlightedIndex, sb.setHighlightedIndex]);
    const handlePrev = useCallback(() => {
        if (hits.length === 0) {
            sb.setHighlightedIndex(-1);
            return;
        }
        if (sb.highlightedIndex <= 0) {
            sb.setHighlightedIndex(0);
            return;
        }
        sb.setHighlightedIndex(sb.highlightedIndex - 1);
    }, [hits.length, sb.highlightedIndex, sb.setHighlightedIndex]);
    useEffect(() => {
        return registerShortcuts({}, {
            isOpen: () => isOpenRef.current,
            isInputFocused: () => document.activeElement === inputRef.current,
            onClose,
            onNext: handleNext,
            onPrev: handlePrev,
            onSelect: handleSelect,
            onNewTab: handleNewTab,
        });
    }, [onClose, handleNext, handlePrev, handleSelect, handleNewTab]);
    // Group hits by source for grouped rendering.
    const grouped = useMemo(() => {
        const map = new Map();
        for (const h of hits) {
            const arr = map.get(h.sourceId) ?? [];
            arr.push(h);
            map.set(h.sourceId, arr);
        }
        return map;
    }, [hits]);
    if (!open)
        return null;
    return (_jsx("div", { role: "dialog", "aria-modal": "true", "aria-label": "Search", onClick: onClose, style: {
            position: "fixed",
            inset: 0,
            zIndex: 50,
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            paddingTop: "12vh",
        }, children: _jsxs("div", { onClick: (e) => e.stopPropagation(), style: {
                width: "100%",
                maxWidth: "640px",
                margin: "0 16px",
                borderRadius: "12px",
                backgroundColor: "var(--cleic-search-bar-bg, white)",
                boxShadow: "0 24px 64px rgba(15, 23, 42, 0.25)",
                overflow: "hidden",
                color: "var(--cleic-search-bar-fg, #0f172a)",
            }, children: [_jsxs("div", { role: "combobox", "aria-expanded": hits.length > 0, "aria-controls": listboxId, "aria-haspopup": "listbox", style: {
                        display: "flex",
                        alignItems: "center",
                        padding: "14px 16px",
                        borderBottom: "1px solid var(--cleic-search-bar-line, #e5e7eb)",
                        gap: "10px",
                    }, children: [_jsx("span", { "aria-hidden": "true", style: { opacity: 0.6 }, children: "\uD83D\uDD0E" }), _jsx("input", { ref: inputRef, type: "text", value: sb.query, onChange: (e) => sb.setQuery(e.target.value), placeholder: placeholder, "aria-label": "Search query", "aria-autocomplete": "list", "aria-controls": listboxId, "aria-activedescendant": active
                                ? `${listboxId}-${active.sourceId}-${active.recordId}`
                                : undefined, style: {
                                flex: 1,
                                border: "none",
                                outline: "none",
                                background: "transparent",
                                fontSize: "16px",
                                color: "inherit",
                            } }), sb.loading && (_jsx("span", { style: { fontSize: "12px", opacity: 0.6 }, children: "Loading\u2026" })), _jsx("button", { onClick: onClose, "aria-label": "Close search", style: {
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                fontSize: "18px",
                                opacity: 0.5,
                            }, children: "\u00D7" })] }), _jsx("div", { "aria-live": "polite", "aria-atomic": "true", style: { position: "absolute", left: "-9999px" }, children: sb.loading
                        ? "Loading"
                        : sb.error
                            ? `Error: ${sb.error}`
                            : sb.query.trim() === ""
                                ? ""
                                : `${hits.length} result${hits.length === 1 ? "" : "s"}` }), _jsxs("ul", { id: listboxId, role: "listbox", "aria-label": "Search results", style: {
                        listStyle: "none",
                        margin: 0,
                        padding: "8px 0",
                        maxHeight: "60vh",
                        overflowY: "auto",
                    }, children: [sb.error && (_jsx("li", { style: {
                                padding: "16px",
                                color: "var(--cleic-search-bar-error, #b91c1c)",
                                fontSize: "13px",
                            }, children: sb.error })), !sb.error && sb.query.trim() !== "" && hits.length === 0 && !sb.loading && (_jsxs("li", { style: {
                                padding: "32px 16px",
                                textAlign: "center",
                                opacity: 0.6,
                                fontSize: "13px",
                            }, children: ["No results for \"", sb.query, "\""] })), [...grouped.entries()].map(([sourceId, sourceHits]) => {
                            const visible = sourceHits.slice(0, perGroup);
                            return (_jsxs("li", { children: [_jsx("p", { style: {
                                            padding: "8px 16px 4px",
                                            margin: 0,
                                            fontSize: "10px",
                                            fontWeight: 700,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.06em",
                                            opacity: 0.55,
                                        }, children: sourceId }), _jsx("ul", { role: "group", style: { listStyle: "none", margin: 0, padding: 0 }, children: visible.map((hit) => {
                                            const isActive = active?.sourceId === hit.sourceId &&
                                                active?.recordId === hit.recordId;
                                            const isAction = hit.kind === "action";
                                            return (_jsxs("li", { id: `${listboxId}-${hit.sourceId}-${hit.recordId}`, role: "option", "aria-selected": isActive, onClick: () => {
                                                    handleOpen(hit);
                                                }, onMouseDown: (e) => {
                                                    if (e.button === 1 && actionHref(hit)) {
                                                        e.preventDefault();
                                                        handleOpenNewTab(hit);
                                                    }
                                                }, onMouseEnter: () => {
                                                    const idx = hits.indexOf(hit);
                                                    if (idx >= 0)
                                                        sb.setHighlightedIndex(idx);
                                                }, style: {
                                                    padding: "10px 16px",
                                                    cursor: "pointer",
                                                    backgroundColor: isActive
                                                        ? "var(--cleic-search-bar-hover, #eff6ff)"
                                                        : "transparent",
                                                    color: isActive
                                                        ? "var(--cleic-search-bar-hover-fg, #1d4ed8)"
                                                        : "inherit",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: "2px",
                                                }, children: [_jsxs("span", { style: {
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                            gap: "8px",
                                                            fontSize: "14px",
                                                            fontWeight: 500,
                                                        }, children: [_jsx("span", { children: hit.primary }), isAction && (_jsx("span", { style: {
                                                                    borderRadius: "999px",
                                                                    border: "1px solid var(--cleic-search-bar-line, #e5e7eb)",
                                                                    padding: "1px 6px",
                                                                    fontSize: "10px",
                                                                    fontWeight: 700,
                                                                    textTransform: "uppercase",
                                                                    opacity: 0.72,
                                                                    whiteSpace: "nowrap",
                                                                }, children: hit.quickAdd ? "Quick add" : hit.action?.intent ?? "Action" }))] }), hit.secondary && (_jsx("span", { style: { fontSize: "12px", opacity: 0.7 }, children: hit.secondary }))] }, `${hit.sourceId}-${hit.recordId}`));
                                        }) })] }, sourceId));
                        })] }), _jsxs("div", { style: {
                        padding: "8px 16px",
                        borderTop: "1px solid var(--cleic-search-bar-line, #e5e7eb)",
                        fontSize: "11px",
                        opacity: 0.6,
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "12px",
                    }, children: [_jsx("span", { children: TIP_TEXT }), _jsxs("span", { style: { fontFamily: "monospace" }, children: [modLabel(), "+K"] })] })] }) }));
}
//# sourceMappingURL=SearchBar.js.map